import { useCallback } from 'react';
import {
  useToastController,
  Toast,
  ToastTitle,
  ToastBody,
  ToastIntent,
} from '@fluentui/react-components';
import { createElement } from 'react';
import type { TaxReturnStatus } from '../types';

export const NOTIFICATION_TOASTER_ID = 'notification-toaster';

/**
 * Map of status transitions that trigger automatic client notifications.
 * Based on the backend NotificationService.GetNotificationsForTransition().
 */
const STATUS_NOTIFICATION_MAP: Record<string, {
  recipientLabel: string;
  channel: 'email & SMS' | 'email';
  description: string;
}> = {
  documents_pending: {
    recipientLabel: 'client',
    channel: 'email & SMS',
    description: 'Missing documents reminder sent',
  },
  in_preparation: {
    // This could be "documents received" to preparer or "changes needed" to preparer
    recipientLabel: 'preparer',
    channel: 'email',
    description: 'Preparer notified',
  },
  in_review: {
    recipientLabel: 'reviewer',
    channel: 'email',
    description: 'Reviewer notified - ready for review',
  },
  ready_for_signature: {
    recipientLabel: 'client',
    channel: 'email & SMS',
    description: 'Signature request notification sent',
  },
  ready_for_efile: {
    recipientLabel: 'reviewer',
    channel: 'email',
    description: 'Reviewer notified - ready for e-file',
  },
  e_filed: {
    recipientLabel: 'client',
    channel: 'email & SMS',
    description: 'E-file status notification sent',
  },
};

/**
 * Returns whether a status change triggers an auto-notification,
 * and details about who was notified.
 */
export function getAutoNotificationInfo(toStatus: TaxReturnStatus): {
  triggered: boolean;
  recipientLabel: string;
  channel: string;
  description: string;
} | null {
  const info = STATUS_NOTIFICATION_MAP[toStatus];
  if (!info) return null;
  return { triggered: true, ...info };
}

/**
 * Hook that provides toast notification helpers for the tax return detail page.
 * Must be used within a component that is a descendant of a Toaster with the matching ID.
 */
export function useNotificationToast() {
  const { dispatchToast } = useToastController(NOTIFICATION_TOASTER_ID);

  const showToast = useCallback(
    (title: string, body: string, intent: ToastIntent = 'success', timeout = 5000) => {
      dispatchToast(
        createElement(Toast, null,
          createElement(ToastTitle, null, title),
          createElement(ToastBody, null, body),
        ),
        { intent, timeout, position: 'top-end' }
      );
    },
    [dispatchToast]
  );

  /**
   * Show a toast when a status change triggers an auto-notification.
   */
  const showStatusChangeNotification = useCallback(
    (toStatus: TaxReturnStatus, clientName: string) => {
      const info = getAutoNotificationInfo(toStatus);
      if (!info) return;

      const capitalizedRecipient = info.recipientLabel.charAt(0).toUpperCase() + info.recipientLabel.slice(1);
      showToast(
        `${capitalizedRecipient} Notified`,
        `${info.description} to ${clientName} via ${info.channel}`,
        'success',
        6000
      );
    },
    [showToast]
  );

  /**
   * Show a toast for a manually triggered notification (document reminder, signature resend, etc.)
   */
  const showManualNotification = useCallback(
    (action: string, clientName: string, channel?: string) => {
      showToast(
        'Notification Sent',
        `${action} sent to ${clientName}${channel ? ` via ${channel}` : ''}`,
        'success',
        5000
      );
    },
    [showToast]
  );

  /**
   * Show an error toast when a notification fails.
   */
  const showNotificationError = useCallback(
    (action: string) => {
      showToast(
        'Notification Failed',
        `Failed to send ${action}. Check the notification history for details.`,
        'error',
        8000
      );
    },
    [showToast]
  );

  return {
    showToast,
    showStatusChangeNotification,
    showManualNotification,
    showNotificationError,
  };
}
